import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { StandardLayoutHeaderWithPortal } from "@/react/components/Layout/StandardLayoutV2/StandardLayoutHeader/StandardLayoutHeaderWithPortal";
import { SpaceGroupHeaderActions } from "@/react/components/SpaceGroups/Header/SpaceGroupHeaderActions";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { PageMetaData } from "@circle-react/components/Layout/PageMetaData";
import { StandardLayout } from "@circle-react/components/Layout/StandardLayout";
import {
  useSpaceGroupResource,
  useSpaceGroupsContext,
  useSpacesContext,
} from "@circle-react/contexts";
import { addSpacesToGroup } from "@circle-react/helpers/spaceGroupHelpers";
import type { Space } from "@circle-react/types";
import { LoaderRow } from "../shared/LoaderRow";
import { SpaceGroupHeaderTitle } from "./Header/SpaceGroupHeaderTitle";
import { Routes } from "./Routes";

export const spacesWithDetailsQueryKey = (spaceGroupId: number | undefined) => [
  "spaces-with-details",
  spaceGroupId,
];

interface Params {
  spaceGroupSlug?: string;
}

export const SpaceGroups = () => {
  const { spaceGroupSlug } = useParams<Params>();
  const {
    helpers: {
      isDataLoading: isSpaceGroupsDataLoading,
      isDataRefreshing: isSpaceGroupsDataRefreshing,
      findBy,
    },
  } = useSpaceGroupsContext();
  const {
    records: spaces,
    helpers: { isDataLoading: isSpacesDataLoading },
  } = useSpacesContext();

  const spaceGroupWithoutSpace = findBy({ slug: spaceGroupSlug });
  const spaceGroupId = spaceGroupWithoutSpace?.id;
  const { data: spaceGroupDetails } = useSpaceGroupResource(spaceGroupId);

  const { data: spacesWithDetailsResponse, isLoading } = useQuery({
    queryKey: spacesWithDetailsQueryKey(spaceGroupWithoutSpace?.id),
    queryFn: () =>
      reactQueryGet<{ records: Space[] }>(
        internalApi.spaces.index({
          params: {
            space_group_id: spaceGroupWithoutSpace?.id,
            include_details: true,
          },
        }),
      ),
    enabled: !!spaceGroupWithoutSpace?.id,
  });
  const spacesWithDetails = spacesWithDetailsResponse?.records || [];

  if (
    isSpaceGroupsDataRefreshing() ||
    isSpaceGroupsDataLoading() ||
    isSpacesDataLoading() ||
    isLoading
  ) {
    return <LoaderRow />;
  }

  const spaceGroup = addSpacesToGroup({
    spaceGroup: { ...spaceGroupWithoutSpace, ...spaceGroupDetails },
    spaces,
    spacesWithDetails,
  });

  return (
    <>
      <PageMetaData
        title={spaceGroup.name}
        bodyClassNames={`view-space view-space-group view-space-group--${spaceGroup.id}`}
      />
      <StandardLayout.Breadcrumb />
      <StandardLayoutHeaderWithPortal
        leftContent={
          <SpaceGroupHeaderTitle spaceGroup={spaceGroup} hasDropdown={false} />
        }
        rightContent={<SpaceGroupHeaderActions spaceGroup={spaceGroup} />}
      />
      <StandardLayout.Content>
        <Routes spaceGroup={spaceGroup} />
      </StandardLayout.Content>
    </>
  );
};

export default SpaceGroups;
